import { withReducer } from 'recompose';
import { normalize, schema } from 'normalizr';
import {
  __,
  assoc,
  assocPath,
  clone,
  concat,
  contains,
  difference,
  equals,
  evolve,
  findLastIndex,
  indexOf,
  insertAll,
  isEmpty,
  last,
  merge,
  nth,
  omit,
  pipe,
  prepend,
  reject,
  reverse,
  sort,
  subtract,
  uniq,
} from 'ramda';
import { getItem, selectItemToJump } from './withItemsReducer.selectors';
import { isFolder } from '../../media-manager-sdk/items';

import { ItemsAction, TEntities, TItemsReducerAction, TState } from './withItemsReducer.types';
import { MediaManagerSDKAnyItem, MediaManagerSDKFolder } from '../../media-manager-sdk';
import { MainSource } from '../../../constants/Source';
import { ItemType } from '../../../constants/ItemType';

const itemSchema = new schema.Entity('items');

const normalizeItem = (item) => normalize(item, itemSchema);
const normalizeItems = (items) => normalize(items, [itemSchema]);
const mergeItemsEntities = (items) => merge(__, items);

const emptyItems = [];
const emptySelectedItems = [];
const emptyLastSelectedItems = [];

const initialState = ({ items, isLoading = false }): TState => {
  const { entities, result } = normalizeItems(items);

  if (!entities.items) {
    entities.items = {};
  }

  return {
    entities,
    items: result,
    isLoading,
    selectedItems: emptySelectedItems,
    editingItem: null,
    newFolderId: null,
    lastSelectedItemId: null,
    lastSelectedItems: emptyLastSelectedItems,
  };
};

const reducer = (state: TState, action: TItemsReducerAction): TState => {
  const { type, payload = {} } = action;
  const { entities, items, selectedItems, editingItem, newFolderId, lastSelectedItemId, lastSelectedItems } = state;

  switch (type) {
    case ItemsAction.Reset: {
      return initialState({ items: payload.items, isLoading: payload.isLoading });
    }

    case ItemsAction.AppendItems: {
      const normalized = normalizeItems(payload.items);
      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq([...items, ...normalized.result]),
      };
    }

    case ItemsAction.PrependItems: {
      const normalized = normalizeItems(payload.items);
      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq([...normalized.result, ...items]),
      };
    }

    case ItemsAction.InsertAfterFolders: {
      const normalized = normalizeItems(payload.items);
      const lastFolderIndex = findLastIndex((itemId) => isFolder(getItem(state)(itemId)), items);

      return {
        ...state,
        entities: evolve<TEntities, TEntities>({ items: mergeItemsEntities(normalized.entities.items) }, entities),
        items: uniq(insertAll<string, string>(lastFolderIndex + 1, normalized.result, items)),
      };
    }

    case ItemsAction.RemoveItems: {
      const itemsToRemove = normalizeItems(payload.items).result;

      const newItems = difference(items, itemsToRemove);

      let newSelectedItems = [];
      if (payload.shouldSelectNextItem) {
        const firstSelectedItemIndex = indexOf(itemsToRemove[0], items);
        newSelectedItems = [newItems[firstSelectedItemIndex] ? newItems[firstSelectedItemIndex] : last(newItems)];
      }

      return {
        ...state,
        items: newItems,
        selectedItems: newSelectedItems,
        lastSelectedItemId: null,
        lastSelectedItems: emptyLastSelectedItems,
      };
    }

    case ItemsAction.ClearItemsSelection: {
      const newState = { ...state, lastSelectedItemId: null };

      if (!isEmpty(selectedItems)) {
        newState.selectedItems = emptySelectedItems;
      }

      if (!isEmpty(lastSelectedItems)) {
        newState.lastSelectedItems = emptyLastSelectedItems;
      }

      return newState;
    }

    case ItemsAction.SelectItems: {
      if (equals(payload.itemIds, selectedItems)) {
        return state;
      }

      return { ...state, selectedItems: payload.itemIds, lastSelectedItemId: payload.itemIds[0] };
    }

    case ItemsAction.SelectItemsUntil: {
      const { itemId } = payload;
      const itemIndices = sort(subtract, [
        indexOf(itemId, items),
        lastSelectedItemId ? indexOf(lastSelectedItemId, items) : 0,
      ]);
      const itemsToSelect = items.slice(itemIndices[0], itemIndices[1] + 1);

      return {
        ...state,
        selectedItems: pipe(difference(__, lastSelectedItems), concat(__, itemsToSelect), uniq)(selectedItems),
        lastSelectedItems: itemsToSelect,
      };
    }

    case ItemsAction.EditItemCancel:
      return { ...state, editingItem: null };

    case ItemsAction.EditItemStart: {
      const editingItem = clone(entities.items[payload.itemId]);

      return { ...state, editingItem };
    }

    case ItemsAction.ClearAll:
      return initialState({ items: emptyItems });

    case ItemsAction.EditItemComplete:
      return {
        ...state,
        entities: assocPath(['items', editingItem.id], editingItem, entities),
        editingItem: null,
      };

    case ItemsAction.CreateNewFolderStart: {
      const { newFolderId } = payload;
      const editingItem: MediaManagerSDKFolder = {
        id: newFolderId,
        name: '',
        isNew: true,
        kind: ItemType.Folder,
        source: MainSource.SiteFiles,
        siteId: null,
      };
      const normalized = normalizeItem(editingItem);

      return {
        ...state,
        entities: evolve<TEntities, TEntities>(
          {
            items: pipe(assoc(newFolderId, editingItem), mergeItemsEntities(normalized.entities.items)),
          },
          entities,
        ),
        items: prepend(newFolderId, items),
        newFolderId,
        editingItem,
      };
    }

    case ItemsAction.CreateNewFolderCancel: {
      const { id } = editingItem;

      return {
        ...state,
        items: difference(items, [id]),
        entities: evolve<TEntities, TEntities>({ items: omit([id]) }, entities),
        editingItem: null,
        newFolderId: null,
      };
    }

    case ItemsAction.JumpToNextItem: {
      const { withShift, jumpTo } = payload;

      if (isEmpty(items)) {
        return state;
      }

      const nextItemId =
        withShift || (!withShift && selectedItems.length > 1) ? last(state.selectedItems) : selectedItems[0];

      const itemToJump = selectItemToJump({
        itemId: nextItemId,
        items,
        jumpTo,
        entities,
      });

      if (!itemToJump) {
        return state;
      }

      if (!withShift) {
        return { ...state, selectedItems: [itemToJump.id] };
      }

      const positionOfNewItem = indexOf(itemToJump.id, selectedItems);

      if (positionOfNewItem === -1) {
        const newSelection = [...selectedItems, itemToJump.id];

        const start = indexOf(nth(0, newSelection), items);
        const end = indexOf(last(newSelection), items);

        return {
          ...state,
          selectedItems: start < end ? items.slice(start, end + 1) : reverse(items.slice(end, start + 1)),
        };
      }

      return { ...state, selectedItems: [...selectedItems.slice(0, positionOfNewItem), itemToJump.id] };
    }

    case ItemsAction.CreateNewFolderComplete: {
      const { updatedItem } = payload;
      const updatedFolderId = updatedItem.id;

      return {
        ...state,
        items: difference(prepend(updatedFolderId, items), [newFolderId]),
        // @ts-expect-error
        entities: evolve<TEntities, TEntities>(
          {
            items: pipe(omit([newFolderId]), assoc(updatedFolderId, updatedItem)),
          },
          entities,
        ),
        selectedItems: [updatedFolderId],
        newFolderId: null,
      };
    }

    case ItemsAction.UpdateEditingItem:
      return { ...state, editingItem: merge<MediaManagerSDKAnyItem, any>(editingItem, payload.updatedItem) };

    case ItemsAction.UpdateLocalItem: {
      const { itemId, updatedItem } = payload;
      const localItem = entities.items[itemId];

      return contains(itemId, items)
        ? {
            ...state,
            entities: assocPath(['items', itemId], merge(localItem, updatedItem), entities),
          }
        : state;
    }

    case ItemsAction.AddItemToSelection:
      return { ...state, selectedItems: uniq([...selectedItems, payload.itemId]), lastSelectedItemId: payload.itemId };

    case ItemsAction.RemoveItemFromSelection:
      return { ...state, selectedItems: reject(equals(payload.itemId), selectedItems) };

    default:
      return state;
  }
};

export const reducerHOC = withReducer('itemsState', 'modifyItemsState', reducer, initialState);
